import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useDebouncedCallback } from 'use-debounce';
import { Checkbox, Grid, IconButton, Input, Paper, Stack } from '@mui/material';
import { Cancel as CancelIcon, Done as SaveIcon, Edit as EditIcon } from '@mui/icons-material';

import { Table } from '../../../components/table/table';
import { Filter } from '../../../components/filter/filter';
import { FilterField } from '../../../components/filter/types';
import { HeadCell, Row, RowCell } from '../../../components/table/types';

import { UpdateCountryDTO, useGetCountriesQuery, useUpdateCountryMutation } from '../../../api';
import type { Country } from '../../../types';
import { permissionsCheck } from '../../../utils/permission';

export const Countries: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const hasPermissionToEditCountries = permissionsCheck(['WRITE_COUNTRIES']);

  // api
  const { data: countries, isLoading: isCountriesLoading } = useGetCountriesQuery();
  const [updateCountry, { isLoading: isUpdateCountryLoading }] = useUpdateCountryMutation();

  // variables
  const isLoading = isCountriesLoading || isUpdateCountryLoading;
  const newNameRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [filteredCountries, setFilteredCountries] = useState<Country[] | undefined>([]);

  // set countries to local state for filtering
  useEffect(() => {
    if (isCountriesLoading) return;
    if (!countries) return;

    const searchTerm = searchInputRef.current?.value;
    if (searchTerm) {
      if (searchTerm === '') setFilteredCountries(countries.countries);
      const filtered = countries.countries?.filter((c) =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries(countries.countries);
    }
  }, [countries, isCountriesLoading, searchInputRef.current?.value]);

  // edit mode logic
  const [activeRowAlpha2Code, setActiveRowAlpha2Code] = useState('');
  const [isEditMode, setEditMode] = useState(false);
  const startEditMode = (name: string, alpha2code: string) => {
    setActiveRowAlpha2Code(alpha2code);
    setEditMode(true);
  };
  const offEditMode = () => {
    setActiveRowAlpha2Code('');
    setEditMode(false);
  };

  // edit country handler
  const editCountry = async (values: UpdateCountryDTO) => {
    if (!values || !values.body.name) return;

    try {
      await updateCountry(values).unwrap();
      offEditMode();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // status change handler
  const handleCountryStatusChange = (
    e: ChangeEvent<HTMLInputElement>,
    alpha2code: string,
    name: string
  ) => {
    void editCountry({ alpha2code, body: { active: e.target.checked, name } });
  };

  // filter logic
  const handleSearchValue = () => {
    if (!countries || !searchInputRef.current) return;

    const searchTerm = searchInputRef.current.value;
    if (searchTerm === '') setFilteredCountries(countries.countries);

    const filtered = countries.countries.filter((c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCountries(filtered);
  };
  const debouncedHandleSearchValue = useDebouncedCallback(() => {
    handleSearchValue();
  }, 600);

  const filterFields: FilterField[] = [
    {
      name: 'search',
      title: t('profiles.filter.search'),
      component: (
        <Input
          inputRef={searchInputRef}
          name="search"
          onChange={debouncedHandleSearchValue}
          sx={{ minWidth: '280px', maxWidth: '360px' }}
          fullWidth
        />
      ),
    },
  ];

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('countries.head-cells.name') },
    { id: '2', label: t('countries.head-cells.alpha2-code') },
    { id: '3', label: t('countries.head-cells.alpha3-code') },
    {
      id: '4',
      label: t('countries.head-cells.active'),
      hidden: !hasPermissionToEditCountries,
    },
  ];

  const rows: Row[] | undefined = filteredCountries?.map((it) => {
    const { name, alpha2code, alpha3code, active } = it;

    const cells: RowCell[] = [
      {
        name: 'name',
        value: name,
        component: isEditMode ? (
          activeRowAlpha2Code === alpha2code ? (
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Input
                inputRef={newNameRef}
                name="name"
                defaultValue={name}
                fullWidth
                disabled={isLoading}
              />

              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton
                  aria-label="save"
                  color="success"
                  onClick={() =>
                    editCountry({
                      alpha2code,
                      body: { name: newNameRef.current?.value ?? '', active },
                    })
                  }
                  disabled={isLoading}
                >
                  <SaveIcon />
                </IconButton>

                <IconButton
                  aria-label="cancel"
                  color="error"
                  onClick={() => offEditMode()}
                  disabled={isLoading}
                >
                  <CancelIcon />
                </IconButton>
              </Stack>
            </Stack>
          ) : undefined
        ) : (
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            {name}
            <IconButton
              aria-label="edit"
              color="warning"
              onClick={() => startEditMode(name, alpha2code)}
              disabled={isLoading}
            >
              <EditIcon />
            </IconButton>
          </Stack>
        ),
      },
      { name: 'alpha2code', value: alpha2code },
      { name: 'alpha3code', value: alpha3code },
      {
        name: 'active',
        hidden: !hasPermissionToEditCountries,
        value: active,
        component: (
          <Checkbox
            checked={active}
            onChange={(e) => handleCountryStatusChange(e, alpha2code, name)}
            disabled={isLoading}
          />
        ),
      },
    ];

    return { cells };
  });

  return (
    <Stack sx={{ gap: 2 }}>
      <Grid size={{ xs: 12 }}>
        <Filter filterFields={filterFields} />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>
    </Stack>
  );
};
